import { useEffect, useRef, useState } from 'react'
import { AggregatedQuoteResult, QuoteRequest, fetchBestQuote } from '../aggregator'

/**
 * Live best-price quote across every configured liquidity source.
 *
 * Three things this has to get right, because quoting runs on every keystroke:
 *
 * - **Debounce.** Typing an amount must not fire a request per character. Aggregators
 *   bill per call and rate-limit.
 * - **Cancellation.** An in-flight quote for a stale input must be aborted, and its
 *   result discarded even if it lands late. Otherwise a slow earlier request can
 *   overwrite a newer one and show a price for an amount the user no longer typed.
 * - **Refresh.** Quotes go stale within seconds. Re-quoting on an interval keeps the
 *   displayed price honest rather than letting it drift silently.
 */

export interface UseAggregatorQuoteOptions {
  debounceMs?: number
  refreshMs?: number
  /** Pause polling, e.g. while a confirmation modal is open. */
  paused?: boolean
}

export interface UseAggregatorQuoteResult {
  data: AggregatedQuoteResult | null
  loading: boolean
  /** Set only when no source produced a usable quote. */
  error: string | null
  refresh: () => void
}

const DEFAULT_DEBOUNCE_MS = 350
const DEFAULT_REFRESH_MS = 15_000

export default function useAggregatorQuote(
  request: QuoteRequest | null,
  options: UseAggregatorQuoteOptions = {}
): UseAggregatorQuoteResult {
  const { debounceMs = DEFAULT_DEBOUNCE_MS, refreshMs = DEFAULT_REFRESH_MS, paused = false } = options

  const [data, setData] = useState<AggregatedQuoteResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  // Serialised so the effect re-runs on a genuine change of inputs rather than on
  // every render that rebuilds an equivalent request object.
  const key = request
    ? [
        request.chainId,
        request.sellToken.address,
        request.buyToken.address,
        request.sellAmount,
        request.slippageBps,
        request.taker ?? '',
        request.recipient ?? '',
      ].join('|')
    : ''

  const requestRef = useRef(request)
  requestRef.current = request

  useEffect(() => {
    if (!key) {
      setData(null)
      setError(null)
      setLoading(false)
      return undefined
    }

    let cancelled = false
    const controller = new AbortController()

    const timer = setTimeout(async () => {
      const { current } = requestRef
      if (!current) return

      setLoading(true)
      try {
        const result = await fetchBestQuote(current, { signal: controller.signal })
        if (cancelled) return

        setData(result)
        setError(
          result.best
            ? null
            : result.failures[0]?.message ?? 'No route available for this pair right now.'
        )
      } catch (err) {
        // fetchBestQuote only rejects if the caller aborted, which is not an error
        // worth showing - a newer request is already on its way.
        if (!cancelled && !controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Could not fetch a quote.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, debounceMs)

    return () => {
      cancelled = true
      controller.abort()
      clearTimeout(timer)
    }
  }, [key, debounceMs, nonce])

  // Periodic refresh so a displayed price cannot silently go stale.
  useEffect(() => {
    if (!key || paused || refreshMs <= 0) return undefined
    const interval = setInterval(() => setNonce((n) => n + 1), refreshMs)
    return () => clearInterval(interval)
  }, [key, paused, refreshMs])

  return { data, loading, error, refresh: () => setNonce((n) => n + 1) }
}
